import { useTranslations } from "use-intl";
import { useFormatters } from "@financialharmony/ui";
import {
  SALES_REPORT_OUTSIDE_FISCAL_YEAR,
  SETTLEMENT_AMOUNT_MISMATCH,
  SUGGESTION_ENQUEUE_FAILED,
  TRANSACTION_ALREADY_SETTLED,
  type CodedError,
} from "@/utils/errorUtils";
import type { PaymentVerification } from "@/types/payments";

/** The toast title for each named refusal the match and mark-as-received flows can get from the server. */
const REFUSAL_TITLE_KEY: Record<string, "transactionAlreadySettled" | "settlementAmountMismatch" | "salesReportOutsideFiscalYear" | "suggestionEnqueueFailed"> = {
  [TRANSACTION_ALREADY_SETTLED]: "transactionAlreadySettled",
  [SETTLEMENT_AMOUNT_MISMATCH]: "settlementAmountMismatch",
  [SALES_REPORT_OUTSIDE_FISCAL_YEAR]: "salesReportOutsideFiscalYear",
  [SUGGESTION_ENQUEUE_FAILED]: "suggestionEnqueueFailed",
};

/** `markAsReceivedFailure`'s toast, with its title in the language the app shows (the description is the server's sentence). */
export function useMarkAsReceivedFailure() {
  const t = useTranslations("consultantMatching.refusals");
  return (error: unknown): { code: string; title: string; description: string } | null => {
    const coded = error as CodedError | null;
    const key = coded?.error ? REFUSAL_TITLE_KEY[coded.error] : undefined;
    const isMarkAsReceived = key === "salesReportOutsideFiscalYear" || key === "suggestionEnqueueFailed";
    return key && isMarkAsReceived ? { code: coded!.error, title: t(key), description: coded!.message } : null;
  };
}

/** The "Transaction already matched"-style title for a named match refusal, else null. */
export function useMatchRefusalTitle() {
  const t = useTranslations("consultantMatching.refusals");
  return (error: unknown): string | null => {
    const coded = error as CodedError | null;
    const key = coded?.error ? REFUSAL_TITLE_KEY[coded.error] : undefined;
    return key === "transactionAlreadySettled" || key === "settlementAmountMismatch" ? t(key) : null;
  };
}

type CorrectionKind = "receivable" | "receipt";

/**
 * The banner a consultant sees when reopening an already-settled receivable or receipt to correct
 * it: what was recorded, then the fixed reminder that confirming adds a history entry.
 */
export function useCorrectionBanner(kind: CorrectionKind) {
  const t = useTranslations("consultantMatching.correction");
  const { formatDate } = useFormatters();
  return (verification: PaymentVerification | null | undefined): string => {
    const tail = t("tail");
    let body: string;
    if (verification?.status === "matched") {
      body = t(`${kind}.matched`);
    } else if (verification?.status === "unmatched_acknowledged") {
      const who = verification.acknowledgedBy ?? t("whoFallback");
      const when = verification.acknowledgedAt ? formatDate(verification.acknowledgedAt, "numeric") : t("whenFallback");
      body = t(`${kind}.acknowledged`, { who, when });
    } else {
      body = t(`${kind}.plain`);
    }
    return [body, tail].join(" ");
  };
}
